import CreateIcon from '@mui/icons-material/Create';
import StarRateIcon from '@mui/icons-material/StarRate';
import { Grid, Stack, Typography } from '@mui/material';
import { Education, Job, Profile } from '@staffedupusa/shared-components';
import dayjs from 'dayjs';
import parse from 'html-react-parser';
import { FC } from 'react';

import { bold, medium } from '@styles/fontWeights';

type ApplicantAdditionalInfoProps = {
  applicationDetails: Profile;
};

export const formatDatesForWork = ({
  dt_started,
  dt_ended,
  current_employer,
}: Job) => {
  const formattedStartDate = dayjs(dt_started).format('MMM YYYY');
  const formattedEndDate = dayjs(dt_ended).format('MMM YYYY');

  if (current_employer) {
    return `${formattedStartDate} - present`;
  }

  if (dt_ended) {
    return `${formattedStartDate} - ${formattedEndDate}`;
  }

  return formattedStartDate;
};

export const formatDatesForEducation = ({
  dt_started,
  dt_ended,
  current_school,
}: Education) => {
  const formattedStartDate = dayjs(dt_started).format('MMM YYYY');
  const formattedEndDate = dayjs(dt_ended).format('MMM YYYY');

  if (dt_ended) {
    return `${formattedStartDate} - ${formattedEndDate} ${current_school ? '(expected)' : ''
      }`;
  }
};

const ApplicantAdditionalInfo: FC<ApplicantAdditionalInfoProps> = ({
  applicationDetails,
}) => {
  const formatDatesForWork = ({
    dt_started,
    dt_ended,
    current_employer,
  }: Job) => {
    const formattedStartDate = dayjs(dt_started).format('MMM YYYY');
    const formattedEndDate = dayjs(dt_ended).format('MMM YYYY');

    if (current_employer) {
      return `${formattedStartDate} - present`;
    }

    if (dt_ended) {
      return `${formattedStartDate} - ${formattedEndDate}`;
    }

    return formattedStartDate;
  };

  const formatDatesForEducation = ({
    dt_started,
    dt_ended,
    current_school,
  }: Education) => {
    const formattedStartDate = dayjs(dt_started).format('MMM YYYY');
    const formattedEndDate = dayjs(dt_ended).format('MMM YYYY');

    if (dt_ended) {
      return `${formattedStartDate} - ${formattedEndDate} ${current_school ? '(expected)' : ''
        }`;
    }
  };

  return (
    <Grid container spacing={2}>

      <Grid item xs={12} md={6}>
        <Stack
          spacing={2}
          p={4}
          height="100%"
          sx={{
            borderColor: 'secondary.main',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: 8,
          }}
        >
          <Typography
            color="secondary"
            variant="h5"
            style={{
              marginBottom: '5px',
              fontWeight: 'bold',
              fontSize: '20px',
            }}
          >
            Work History:
          </Typography>
          {applicationDetails?.work_history?.length > 0 ? (
            <>
              {applicationDetails?.work_history.map((job) => (
                <Stack key={job.id} spacing={1} style={{ marginBottom: '12px' }}>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    fontWeight={bold}
                    fontSize="18px"
                  >
                    {job.employer}
                    <Typography
                      component="span"
                      sx={{ ml: 2 }}
                      // fontStyle="italic"
                      fontSize="14px"
                    >
                      ({formatDatesForWork(job)})
                    </Typography>
                  </Typography>
                  <Typography variant="h4" color="text.secondary" fontSize="14px">
                    {job.job_title}
                  </Typography>
                  {job?.responsibilities ? (
                    <Typography fontSize="14px">
                      {parse(job.responsibilities)}
                    </Typography>
                  ) : null}
                </Stack>
              ))}
            </>
          ) : (
            <Typography color="text.secondary">No work history found.</Typography>
          )}
        </Stack>
      </Grid>
      {/* {(applicationDetails?.education?.length > 0 ||
        applicationDetails?.certifications?.length > 0 ||
        applicationDetails?.certifications?.length > 0) && ( */}
      <Grid item xs={12} md={6}>
        <Stack
          spacing={2}
          p={4}
          height="100%"
          sx={{
            borderColor: 'secondary.main',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderRadius: 8,
          }}
        >
          <Typography
            color="secondary"
            variant="h5"
            style={{
              marginBottom: '5px',
              fontWeight: 'bold',
              fontSize: '20px',
            }}
          >
            Education:
          </Typography>
          {applicationDetails?.education?.length > 0 ? (
            <>
              {applicationDetails.education.map((education) => (
                <Stack key={education.id} spacing={0.5}>
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    fontWeight={bold}
                    fontSize="18px"
                  >
                    {education.school}
                  </Typography>
                  <Typography color="text.secondary" fontStyle="italic">
                    {formatDatesForEducation(education)}
                  </Typography>
                  <Typography
                    variant="h6"
                    component="p"
                    color="text.secondary"
                    fontWeight={bold}
                  >
                    {`${education.degree} ${education.emphasis ? `in ${education.emphasis}` : ''
                      }`}
                  </Typography>
                  {education?.gpa && (
                    <Typography color="text.secondary">{`GPA: ${education.gpa}`}</Typography>
                  )}
                </Stack>
              ))}
            </>
          ) : (
            <Typography color="text.secondary">No education history found.</Typography>
          )}
          <Typography
            color="secondary"
            variant="h5"
            style={{
              marginBottom: '-3px',
              fontWeight: 'bold',
              fontSize: '20px',
            }}
          >
            Certifications:
          </Typography>
          {applicationDetails?.certifications?.length > 0 ? (
            <>
              {applicationDetails?.certifications.map((certificate) => (
                <Typography
                  key={certificate.id}
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  textTransform="capitalize"
                  component="p"
                  fontWeight={medium}
                  fontSize="14px"
                  style={{ marginBottom: '10px' }}
                >
                  <StarRateIcon
                    fontSize="inherit"
                    sx={{ mr: 1 }}
                    color="secondary"
                  />
                  {certificate.label}
                </Typography>
              ))}
            </>
          ) : (
            <Typography color="text.secondary">No certifications found.</Typography>
          )}

          <Typography
            color="secondary"
            variant="h5"
            style={{
              marginBottom: '-5px',
              fontWeight: 'bold',
              fontSize: '20px',
            }}
          >
            Skills:
          </Typography>
          {applicationDetails?.skills?.length > 0 ? (
            <>
              {applicationDetails.skills.map((skill) => (
                <Typography
                  key={skill.id}
                  color="text.secondary"
                  display="flex"
                  alignItems="center"
                  textTransform="capitalize"
                  component="p"
                  fontWeight={medium}
                >
                  <CreateIcon
                    fontSize="inherit"
                    sx={{ mr: 1 }}
                    color="secondary"
                  />
                  {skill.label}
                </Typography>
              ))}
            </>
          ) : (
            <Typography color="text.secondary">No skills found.</Typography>
          )}
        </Stack>
      </Grid>
      {/* )} */}
    </Grid>
  );
};

export default ApplicantAdditionalInfo;
